import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import Pusher, { Channel } from 'pusher-js';
import { useAuth } from './AuthContext';
import { getPusher, disconnectPusher } from '../services/pusherService';

type PusherContextType = {
  pusher: Pusher | null;
  subscribeToChannel: (channelName: string) => Channel | null;
  unsubscribeFromChannel: (channelName: string) => void;
  isConnected: boolean;
};

const PusherContext = createContext<PusherContextType>({
  pusher: null,
  subscribeToChannel: () => null,
  unsubscribeFromChannel: () => {},
  isConnected: false,
});

export const PusherProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const channelsRef = useRef<Map<string, Channel>>(new Map());
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      console.log('[PusherContext] User authenticated, initializing Pusher');
      getPusher().then((pusherInstance) => {
        setPusher(pusherInstance);

        // Monitor connection state
        pusherInstance.connection.bind('connected', () => {
          console.log('[PusherContext] Connection established');
          setIsConnected(true);
        });

        pusherInstance.connection.bind('disconnected', () => {
          console.log('[PusherContext] Connection lost');
          setIsConnected(false);
        });
      });
    } else {
      console.log('[PusherContext] User not authenticated, disconnecting Pusher');
      disconnectPusher();
      setPusher(null);
      setIsConnected(false);
      channelsRef.current.clear();
    }

    return () => {
      console.log('[PusherContext] Cleanup: disconnecting Pusher');
      disconnectPusher();
      channelsRef.current.clear();
    };
  }, [isAuthenticated]);

  // Memoised so their identity only changes when `pusher` changes (once, on
  // connect). Previously these were re-created on every provider render, which
  // made every consumer's subscribe/unsubscribe effect churn — tearing down and
  // re-attaching channel bindings on each `isConnected` toggle and risking a
  // dropped subscription. Stable identities let consumers keep a single steady
  // subscription for the screen's lifetime.
  const subscribeToChannel = useCallback((channelName: string): Channel | null => {
    if (!pusher) {
      console.warn('[PusherContext] Cannot subscribe - Pusher not initialized');
      return null;
    }

    const existing = channelsRef.current.get(channelName);
    if (existing) {
      console.log('[PusherContext] Returning existing channel:', channelName);
      return existing;
    }

    console.log('[PusherContext] Subscribing to channel:', channelName);
    const channel = pusher.subscribe(channelName);

    // Log subscription success/error
    channel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherContext] Successfully subscribed to:', channelName);
    });

    channel.bind('pusher:subscription_error', (error: any) => {
      console.error('[PusherContext] Subscription error for', channelName, ':', error);
    });

    channelsRef.current.set(channelName, channel);
    return channel;
  }, [pusher]);

  const unsubscribeFromChannel = useCallback((channelName: string) => {
    if (!pusher) {
      console.warn('[PusherContext] Cannot unsubscribe - Pusher not initialized');
      return;
    }

    console.log('[PusherContext] Unsubscribing from channel:', channelName);
    pusher.unsubscribe(channelName);
    channelsRef.current.delete(channelName);
  }, [pusher]);

  return (
    <PusherContext.Provider value={{ pusher, subscribeToChannel, unsubscribeFromChannel, isConnected }}>
      {children}
    </PusherContext.Provider>
  );
};

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (!context) {
    throw new Error('usePusher must be used within PusherProvider');
  }
  return context;
};